import { useState, useEffect } from "react";
import { Settings, Save, Loader2, Globe, Palette, FileText, MessageSquare, Image, Plus, Trash2, GripVertical, Eye, EyeOff, Navigation, Users, Plug, Key, ExternalLink, ImageIcon, BarChart3 } from "lucide-react";
import BrandingImageField from "@/components/BrandingImageField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PlatformConfig, HeroSlide, Testimonial, defaultPlatformConfig } from "@/hooks/usePlatformConfig";

interface Props {
  logAction: (action: string, entityType: string, entityId: string, details?: any) => void;
}

const AdminPlatformTab = ({ logAction }: Props) => {
  const { toast } = useToast();
  const [config, setConfig] = useState<PlatformConfig>(defaultPlatformConfig);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeSection, setActiveSection] = useState("identity");

  useEffect(() => {
    const load = async () => {
      // Load public config
      const { data } = await supabase
        .from("platform_settings")
        .select("key, value")
        .eq("key", "platform_config")
        .maybeSingle();
      
      // Load secrets (admin-only)
      const { data: secretsData } = await supabase
        .from("platform_settings")
        .select("key, value")
        .eq("key", "platform_secrets")
        .maybeSingle();
      
      const merged = {
        ...defaultPlatformConfig,
        ...(data?.value as any),
        ...(secretsData?.value as any),
      };
      setConfig(merged);
      setLoaded(true);
    };
    load();
  }, []);

  const saveConfig = async () => {
    setSaving(true);

    // Separate secret keys from public config
    const { canva_api_key, evolution_api_key, evolution_api_url, ...publicConfig } = config as any;
    const secrets = { canva_api_key, evolution_api_key, evolution_api_url };

    // Save public config
    const { data: existing } = await supabase
      .from("platform_settings")
      .select("id")
      .eq("key", "platform_config")
      .maybeSingle();

    let error;
    if (existing) {
      ({ error } = await supabase
        .from("platform_settings")
        .update({ value: publicConfig as any, updated_at: new Date().toISOString() })
        .eq("key", "platform_config"));
    } else {
      ({ error } = await supabase
        .from("platform_settings")
        .insert({ key: "platform_config", value: publicConfig as any }));
    }

    // Save secrets separately
    if (!error) {
      const { data: existingSecrets } = await supabase
        .from("platform_settings")
        .select("id")
        .eq("key", "platform_secrets")
        .maybeSingle();

      if (existingSecrets) {
        ({ error } = await supabase
          .from("platform_settings")
          .update({ value: secrets as any, updated_at: new Date().toISOString() })
          .eq("key", "platform_secrets"));
      } else {
        ({ error } = await supabase
          .from("platform_settings")
          .insert({ key: "platform_secrets", value: secrets as any }));
      }
    }

    if (error) {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
    } else {
      logAction("platform_config_updated", "platform_settings", "platform_config");
      toast({ title: "Configurações salvas!" });
    }
    setSaving(false);
  };

  const addHeroSlide = () => {
    const newSlide: HeroSlide = {
      id: crypto.randomUUID(),
      title: "Novo Slide",
      subtitle: "Descrição do slide",
      cta_text: "Começar Grátis",
      cta_link: "/cadastro",
      image_url: "",
      is_active: true,
    };
    setConfig(c => ({ ...c, hero_slides: [...c.hero_slides, newSlide] }));
  };

  const updateSlide = (id: string, field: keyof HeroSlide, value: any) => {
    setConfig(c => ({
      ...c,
      hero_slides: c.hero_slides.map(s => s.id === id ? { ...s, [field]: value } : s),
    }));
  };

  const removeSlide = (id: string) => {
    setConfig(c => ({ ...c, hero_slides: c.hero_slides.filter(s => s.id !== id) }));
  };

  const addTestimonial = () => {
    const newT: Testimonial = {
      id: crypto.randomUUID(),
      name: "Novo Cliente",
      role: "Cargo — Restaurante",
      text: "Depoimento aqui...",
      rating: 5,
      is_active: true,
    };
    setConfig(c => ({ ...c, testimonials: [...c.testimonials, newT] }));
  };

  const updateTestimonial = (id: string, field: keyof Testimonial, value: any) => {
    setConfig(c => ({
      ...c,
      testimonials: c.testimonials.map(t => t.id === id ? { ...t, [field]: value } : t),
    }));
  };

  const removeTestimonial = (id: string) => {
    setConfig(c => ({ ...c, testimonials: c.testimonials.filter(t => t.id !== id) }));
  };

  const updateNavLink = (index: number, field: string, value: any) => {
    setConfig(c => ({
      ...c,
      nav_links: c.nav_links.map((l, i) => i === index ? { ...l, [field]: value } : l),
    }));
  };

  const addNavLink = () => {
    setConfig(c => ({
      ...c,
      nav_links: [...c.nav_links, { label: "Novo Link", href: "/", is_active: true }],
    }));
  };

  const removeNavLink = (index: number) => {
    setConfig(c => ({ ...c, nav_links: c.nav_links.filter((_, i) => i !== index) }));
  };

  if (!loaded) return <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const sidebarItems = [
    { key: "identity", icon: Globe, label: "Identidade" },
    { key: "branding", icon: ImageIcon, label: "Branding" },
    { key: "navigation", icon: Navigation, label: "Navegação" },
    { key: "hero_slides", icon: Image, label: "Hero Slides" },
    { key: "testimonials", icon: Users, label: "Depoimentos" },
    { key: "sections", icon: FileText, label: "Seções" },
    { key: "social", icon: MessageSquare, label: "Redes Sociais" },
    { key: "public_chat", icon: MessageSquare, label: "Chat Público" },
    { key: "integrations", icon: Plug, label: "Integrações" },
    { key: "analytics", icon: BarChart3, label: "Analytics" },
    { key: "maintenance", icon: Settings, label: "Manutenção" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-lg">Configurações da Plataforma</h2>
          <p className="text-xs text-muted-foreground">Configure todas as seções do site institucional</p>
        </div>
        <Button onClick={saveConfig} disabled={saving} size="sm" className="bg-gradient-primary text-primary-foreground">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar Tudo
        </Button>
      </div>

      <div className="flex gap-4">
        {/* Sidebar */}
        <div className="w-48 shrink-0 space-y-1">
          {sidebarItems.map(item => (
            <button
              key={item.key}
              onClick={() => setActiveSection(item.key)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === item.key ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-secondary'}`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 rounded-xl border border-border bg-card p-5">
          {activeSection === "identity" && (
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-sm mb-4">Identidade da Plataforma</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Nome do Site</Label>
                  <Input value={config.site_name} onChange={e => setConfig(c => ({ ...c, site_name: e.target.value }))} className="h-9 text-sm" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Iniciais do Logo</Label>
                  <Input value={config.logo_initials} onChange={e => setConfig(c => ({ ...c, logo_initials: e.target.value }))} className="h-9 text-sm" maxLength={3} /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Texto do Logo</Label>
                <Input value={config.logo_text} onChange={e => setConfig(c => ({ ...c, logo_text: e.target.value }))} className="h-9 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Descrição do Site</Label>
                <Textarea value={config.site_description} onChange={e => setConfig(c => ({ ...c, site_description: e.target.value }))} className="text-sm min-h-[60px]" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Descrição do Footer</Label>
                <Textarea value={config.footer_description} onChange={e => setConfig(c => ({ ...c, footer_description: e.target.value }))} className="text-sm min-h-[60px]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">E-mail de Contato</Label>
                  <Input value={config.contact_email} onChange={e => setConfig(c => ({ ...c, contact_email: e.target.value }))} className="h-9 text-sm" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Telefone</Label>
                  <Input value={config.contact_phone} onChange={e => setConfig(c => ({ ...c, contact_phone: e.target.value }))} className="h-9 text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">WhatsApp</Label>
                  <Input value={config.contact_whatsapp} onChange={e => setConfig(c => ({ ...c, contact_whatsapp: e.target.value }))} className="h-9 text-sm" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Endereço</Label>
                  <Input value={config.contact_address} onChange={e => setConfig(c => ({ ...c, contact_address: e.target.value }))} className="h-9 text-sm" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Badge do Hero</Label>
                <Input value={config.hero_badge_text} onChange={e => setConfig(c => ({ ...c, hero_badge_text: e.target.value }))} className="h-9 text-sm" /></div>
            </div>
          )}

          {activeSection === "branding" && (
            <div className="space-y-6">
              <h3 className="font-display font-semibold text-sm mb-4">Imagens da Marca</h3>
              <p className="text-xs text-muted-foreground -mt-2 mb-4">Configure as imagens exibidas no header, footer, dashboard e favicon. Você pode fazer upload ou colar uma URL externa.</p>
              
              <BrandingImageField
                label="Logo Principal (Header & Footer)"
                hint="Exibido na navegação principal e no rodapé. Recomendado: PNG transparente, 200×200px"
                value={config.logo_url}
                onChange={url => setConfig(c => ({ ...c, logo_url: url }))}
              />

              <BrandingImageField
                label="Ícone do Dashboard (Sidebar)"
                hint="Exibido no topo da sidebar do painel administrativo. Recomendado: ícone quadrado, 64×64px"
                value={config.dashboard_icon_url}
                onChange={url => setConfig(c => ({ ...c, dashboard_icon_url: url }))}
              />

              <BrandingImageField
                label="Favicon"
                hint="Ícone exibido na aba do navegador. Recomendado: ICO ou PNG, 32×32px ou 64×64px"
                value={config.favicon_url}
                onChange={url => setConfig(c => ({ ...c, favicon_url: url }))}
                previewSize="w-10 h-10"
              />

              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs text-muted-foreground">
                  💡 <strong>Dica:</strong> Se nenhuma imagem for configurada, o sistema usará as iniciais do logo como fallback. O favicon é atualizado dinamicamente ao salvar.
                </p>
              </div>
            </div>
          )}

          {activeSection === "navigation" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-sm">Links de Navegação</h3>
                <Button size="sm" variant="outline" onClick={addNavLink}><Plus className="h-3 w-3 mr-1" />Adicionar</Button>
              </div>
              {config.nav_links.map((link, i) => (
                <div key={i} className="flex items-center gap-2 p-3 rounded-lg border border-border">
                  <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input value={link.label} onChange={e => updateNavLink(i, 'label', e.target.value)} className="h-8 text-sm" placeholder="Label" />
                  <Input value={link.href} onChange={e => updateNavLink(i, 'href', e.target.value)} className="h-8 text-sm" placeholder="URL" />
                  <Switch checked={link.is_active} onCheckedChange={v => updateNavLink(i, 'is_active', v)} />
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => removeNavLink(i)}>
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {activeSection === "hero_slides" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-sm">Hero Slides</h3>
                <Button size="sm" variant="outline" onClick={addHeroSlide}><Plus className="h-3 w-3 mr-1" />Novo Slide</Button>
              </div>
              {config.hero_slides.map((slide) => (
                <div key={slide.id} className="p-4 rounded-lg border border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateSlide(slide.id, 'is_active', !slide.is_active)}>
                        {slide.is_active ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                      </button>
                      <span className="text-sm font-medium">{slide.title.substring(0, 40)}...</span>
                    </div>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeSlide(slide.id)}>
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Título</Label>
                    <Input value={slide.title} onChange={e => updateSlide(slide.id, 'title', e.target.value)} className="h-9 text-sm" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Subtítulo</Label>
                    <Textarea value={slide.subtitle} onChange={e => updateSlide(slide.id, 'subtitle', e.target.value)} className="text-sm min-h-[50px]" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs">Texto do CTA</Label>
                      <Input value={slide.cta_text} onChange={e => updateSlide(slide.id, 'cta_text', e.target.value)} className="h-9 text-sm" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Link do CTA</Label>
                      <Input value={slide.cta_link} onChange={e => updateSlide(slide.id, 'cta_link', e.target.value)} className="h-9 text-sm" /></div>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">URL da Imagem de Fundo</Label>
                    <Input value={slide.image_url} onChange={e => updateSlide(slide.id, 'image_url', e.target.value)} className="h-9 text-sm" placeholder="https://..." /></div>
                </div>
              ))}
            </div>
          )}

          {activeSection === "testimonials" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-sm">Depoimentos</h3>
                <Button size="sm" variant="outline" onClick={addTestimonial}><Plus className="h-3 w-3 mr-1" />Novo Depoimento</Button>
              </div>
              {config.testimonials.map((t) => (
                <div key={t.id} className="p-4 rounded-lg border border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateTestimonial(t.id, 'is_active', !t.is_active)}>
                        {t.is_active ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                      </button>
                      <span className="text-sm font-medium">{t.name}</span>
                    </div>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeTestimonial(t.id)}>
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs">Nome</Label>
                      <Input value={t.name} onChange={e => updateTestimonial(t.id, 'name', e.target.value)} className="h-9 text-sm" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Cargo / Restaurante</Label>
                      <Input value={t.role} onChange={e => updateTestimonial(t.id, 'role', e.target.value)} className="h-9 text-sm" /></div>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Depoimento</Label>
                    <Textarea value={t.text} onChange={e => updateTestimonial(t.id, 'text', e.target.value)} className="text-sm min-h-[50px]" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs">Avaliação (1-5)</Label>
                      <Input type="number" min={1} max={5} value={t.rating} onChange={e => updateTestimonial(t.id, 'rating', parseInt(e.target.value) || 5)} className="h-9 text-sm" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">URL do Avatar</Label>
                      <Input value={t.avatar_url || ''} onChange={e => updateTestimonial(t.id, 'avatar_url', e.target.value)} className="h-9 text-sm" placeholder="https://..." /></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeSection === "sections" && (
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-sm mb-4">Seções Visíveis</h3>
              {[
                { k: "show_features_section" as const, l: "Seção de Funcionalidades" },
                { k: "show_how_it_works_section" as const, l: "Seção Como Funciona" },
                { k: "show_social_proof_section" as const, l: "Seção de Depoimentos" },
                { k: "show_pricing_section" as const, l: "Seção de Preços" },
                { k: "show_cta_section" as const, l: "Seção CTA Final" },
                { k: "show_demo_link" as const, l: "Botão Ver Demo (Hero)" },
              ].map(({ k, l }) => (
                <div key={k} className="flex items-center justify-between py-2">
                  <Label className="text-sm">{l}</Label>
                  <Switch checked={config[k]} onCheckedChange={v => setConfig(c => ({ ...c, [k]: v }))} />
                </div>
              ))}
            </div>
          )}

          {activeSection === "social" && (
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-sm mb-4">Redes Sociais</h3>
              {[
                { k: "social_instagram", l: "Instagram" },
                { k: "social_facebook", l: "Facebook" },
                { k: "social_linkedin", l: "LinkedIn" },
                { k: "social_twitter", l: "Twitter / X" },
                { k: "social_youtube", l: "YouTube" },
                { k: "social_tiktok", l: "TikTok" },
              ].map(({ k, l }) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs">{l}</Label>
                  <Input
                    value={(config as any)[k] || ''}
                    onChange={e => setConfig(c => ({ ...c, [k]: e.target.value }))}
                    className="h-9 text-sm"
                    placeholder={`https://${l.toLowerCase()}.com/...`}
                  />
                </div>
              ))}
            </div>
          )}

          {activeSection === "public_chat" && (
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-sm mb-4">Chat Público (Visitantes)</h3>
              <p className="text-xs text-muted-foreground mb-4">Configure o chat de atendimento exibido nas páginas públicas para potenciais clientes.</p>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label className="text-sm">Ativar Chat Público</Label>
                  <p className="text-xs text-muted-foreground">Exibe o widget de chat na landing page e páginas institucionais</p>
                </div>
                <Switch checked={(config as any).public_chat_enabled ?? false} onCheckedChange={v => setConfig(c => ({ ...c, public_chat_enabled: v }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Mensagem de Boas-vindas</Label>
                <Textarea
                  value={(config as any).public_chat_welcome_message || ''}
                  onChange={e => setConfig(c => ({ ...c, public_chat_welcome_message: e.target.value }))}
                  className="text-sm min-h-[80px]"
                  placeholder="Olá! 👋 Precisa de ajuda? Converse com um de nossos consultores!"
                />
                <p className="text-[10px] text-muted-foreground">Esta mensagem aparece automaticamente após alguns segundos para convidar o visitante a iniciar uma conversa.</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs text-muted-foreground">
                  💡 <strong>Dica:</strong> As conversas dos visitantes aparecerão na aba de Suporte do painel admin para que sua equipe possa responder.
                </p>
              </div>
            </div>
          )}

          {activeSection === "integrations" && (
            <div className="space-y-6">
              <h3 className="font-display font-semibold text-sm mb-4">Integrações Externas</h3>

              {/* Canva */}
              <div className="p-4 rounded-xl border border-border space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                    <Palette className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Canva Connect API</p>
                    <p className="text-xs text-muted-foreground">Editor de imagens integrado para marketing dos restaurantes</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1">
                      <Key className="h-3 w-3" /> API Key do Canva
                    </Label>
                    <Input
                      type="password"
                      value={config.canva_api_key || ''}
                      onChange={e => setConfig(c => ({ ...c, canva_api_key: e.target.value }))}
                      className="h-9 text-sm font-mono"
                      placeholder="cnapi-xxx-xxx..."
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Obtenha em{" "}
                      <a href="https://www.canva.com/developers/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">
                        canva.com/developers <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">URL de Templates da Marca (opcional)</Label>
                    <Input
                      value={config.canva_brand_template_url || ''}
                      onChange={e => setConfig(c => ({ ...c, canva_brand_template_url: e.target.value }))}
                      className="h-9 text-sm"
                      placeholder="https://www.canva.com/brand/..."
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Link direto para a pasta de templates da marca no Canva Pro
                    </p>
                  </div>
                </div>
              </div>

              {/* Evolution API / WhatsApp */}
              <div className="p-4 rounded-xl border border-border space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Evolution API (WhatsApp)</p>
                    <p className="text-xs text-muted-foreground">Integração para notificações, suporte e marketing via WhatsApp</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1">
                      <Globe className="h-3 w-3" /> URL da Instância
                    </Label>
                    <Input
                      value={config.evolution_api_url || ''}
                      onChange={e => setConfig(c => ({ ...c, evolution_api_url: e.target.value }))}
                      className="h-9 text-sm font-mono"
                      placeholder="http://seu-servidor:8080"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1">
                      <Key className="h-3 w-3" /> API Key Global
                    </Label>
                    <Input
                      type="password"
                      value={config.evolution_api_key || ''}
                      onChange={e => setConfig(c => ({ ...c, evolution_api_key: e.target.value }))}
                      className="h-9 text-sm font-mono"
                      placeholder="sua-api-key..."
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Documentação:{" "}
                    <a href="https://doc.evolution-api.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">
                      doc.evolution-api.com <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  </p>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs text-muted-foreground">
                  💡 <strong>Dica:</strong> As credenciais da Evolution API ficam armazenadas como secrets seguros no backend. 
                  Aqui você pode visualizar/referenciar a configuração. Para alterar os secrets, use o painel de configuração do backend.
                </p>
              </div>
            </div>
          )}

          {activeSection === "analytics" && (
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-sm mb-4">Google Analytics & Meta Pixel</h3>
              <p className="text-xs text-muted-foreground mb-4">
                Configure o rastreamento global da plataforma. Esses IDs serão usados em <strong>todas as páginas do site institucional</strong> (landing, cadastro, login, planos etc.). 
                Eventos de <strong>page_view</strong> são enviados automaticamente a cada navegação.
              </p>

              <div className="p-4 rounded-xl border border-border space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-sky-500/20 flex items-center justify-center">
                    <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Google Analytics 4</p>
                    <p className="text-xs text-muted-foreground">Rastreamento de tráfego, conversões e comportamento</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Measurement ID</Label>
                  <Input
                    value={config.platform_ga_measurement_id || ''}
                    onChange={e => setConfig(c => ({ ...c, platform_ga_measurement_id: e.target.value }))}
                    className="h-9 text-sm font-mono"
                    placeholder="G-XXXXXXXXXX"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Encontre em{" "}
                    <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">
                      Google Analytics <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                    {" → Admin → Data Streams → Measurement ID"}
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-xl border border-border space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center">
                    <Globe className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Meta Pixel (Facebook / Instagram)</p>
                    <p className="text-xs text-muted-foreground">Rastreamento de conversões para anúncios Meta</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Pixel ID</Label>
                  <Input
                    value={config.platform_meta_pixel_id || ''}
                    onChange={e => setConfig(c => ({ ...c, platform_meta_pixel_id: e.target.value }))}
                    className="h-9 text-sm font-mono"
                    placeholder="123456789012345"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Encontre em{" "}
                    <a href="https://business.facebook.com/events_manager" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">
                      Meta Events Manager <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                    {" → Fontes de Dados → Pixel ID"}
                  </p>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <p className="text-xs text-muted-foreground">
                  📊 <strong>Eventos rastreados automaticamente:</strong>
                </p>
                <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5">
                  <li><code className="text-[10px] bg-muted px-1 rounded">page_view</code> — cada navegação de página</li>
                  <li><code className="text-[10px] bg-muted px-1 rounded">sign_up</code> — cadastro de novo restaurante</li>
                  <li><code className="text-[10px] bg-muted px-1 rounded">login</code> — login de usuários</li>
                </ul>
                <p className="text-[10px] text-muted-foreground mt-2">
                  💡 Restaurantes podem configurar seus próprios IDs de GA/Pixel nas configurações individuais para rastrear o cardápio digital.
                </p>
              </div>
            </div>
          )}

          {activeSection === "maintenance" && (
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-sm mb-4">Manutenção</h3>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">Modo Manutenção</Label>
                  <p className="text-xs text-muted-foreground">Desativa o acesso público ao site</p>
                </div>
                <Switch checked={config.maintenance_mode} onCheckedChange={v => setConfig(c => ({ ...c, maintenance_mode: v }))} />
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Mensagem de Manutenção</Label>
                <Textarea value={config.maintenance_message} onChange={e => setConfig(c => ({ ...c, maintenance_message: e.target.value }))} className="text-sm min-h-[80px]" /></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPlatformTab;
